import config from "../../Configurations";

export { formatSeconds, videoDurationLimits } from "./surveyWidgetFlow";

// Video answers (question type "video"). The API opens a Cloud Storage resumable upload
// (POST /feedback/public/video-upload) and the browser sends the file straight to the bucket in
// chunks. After a network error it asks the session how far it got and carries on from there.
// See pivony-api api/utils/survey_video.py.

export const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
export const VIDEO_FILE_ACCEPT = "video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm";
const RETRIES = 6;

/** "video/webm;codecs=vp8,opus" → "video/webm"; "" for a type the API does not take. */
export function baseVideoType(mime, fileName = "") {
  const base = String(mime || "").split(";")[0].trim().toLowerCase();
  if (VIDEO_TYPES.includes(base)) return base;
  // Some Android file pickers leave the type empty.
  const ext = String(fileName).toLowerCase().split(".").pop();
  return { mp4: "video/mp4", mov: "video/quicktime", webm: "video/webm" }[ext] || "";
}

/** The first recording format this browser supports: mp4 on iPhone Safari, webm on Chrome and Firefox. */
export function pickRecorderType() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  const candidates = ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"];
  const isSupported = window.MediaRecorder.isTypeSupported || (() => false);
  return candidates.find((type) => isSupported.call(window.MediaRecorder, type)) || "";
}

export function canRecord() {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    !!window.MediaRecorder
  );
}

/** A picked file's length in seconds, read from its metadata (null when the browser can't tell). */
export function readVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (value) => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(value) && value > 0 ? value : null);
    };
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => done(video.duration);
    video.onerror = () => done(null);
    video.src = url;
  });
}

export async function requestVideoUpload(payload) {
  const root = (config.BASE_URL || "").replace(/\/+$/, "");
  const res = await fetch(`${root}/feedback/public/video-upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

// "bytes=0-524287" → 524288 (the next byte to send); no header means nothing is stored yet.
function nextOffset(response) {
  const match = /bytes=0-(\d+)/.exec(response.headers.get("Range") || "");
  return match ? Number(match[1]) + 1 : 0;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload ``blob`` to a resumable session. Calls onProgress(0..1). Resolves when the object is stored;
 * rejects after RETRIES failed attempts in a row, or at once when the session is gone (4xx).
 * Calling it again with the same session resumes where the last call stopped.
 */
export async function uploadInChunks({
  uploadUrl,
  blob,
  chunkBytes,
  onProgress = () => {},
  fetchImpl = (...args) => fetch(...args),
  sleep = wait,
}) {
  const size = blob.size;
  const put = (headers, body) => fetchImpl(uploadUrl, { method: "PUT", headers, body });
  let offset = 0;
  let failures = 0;
  let resumeFirst = true;
  while (true) {
    try {
      let response;
      if (resumeFirst) {
        // Ask the session what it already has (a fresh one answers 308 with no Range).
        response = await put({ "Content-Range": `bytes */${size}` });
      } else {
        const end = Math.min(offset + chunkBytes, size);
        response = await put({ "Content-Range": `bytes ${offset}-${end - 1}/${size}` }, blob.slice(offset, end));
      }
      if (response.status === 200 || response.status === 201) {
        onProgress(1);
        return;
      }
      if (response.status === 308) {
        const sent = !resumeFirst;
        const before = offset;
        offset = nextOffset(response);
        onProgress(size ? offset / size : 0);
        resumeFirst = false;
        if (sent && offset <= before) throw new Error("upload made no progress");
        failures = 0;
        continue;
      }
      if (response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) {
        const error = new Error(`upload refused (${response.status})`);
        error.fatal = true;
        throw error;
      }
      throw new Error(`upload failed (${response.status})`);
    } catch (error) {
      if (error.fatal) throw error;
      failures += 1;
      if (failures > RETRIES) throw error;
      resumeFirst = true;
      await sleep(Math.min(1000 * 2 ** (failures - 1), 15000));
    }
  }
}
